import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Clock, CheckCircle, AlertCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { NewsletterPreview } from "@/components/preview/NewsletterPreview";
import { useToast } from "@/hooks/use-toast";

export const NewsletterReviewSpace = () => {
  const { toast } = useToast();
  const today = new Date().toISOString().split('T')[0];
  
  const { data: newsletters, isLoading } = useQuery({
    queryKey: ['newsletters-for-review', today],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('daily_newsletters')
        .select('*')
        .eq('scheduled_date', today)
        .eq('status', 'pending_review')
        .order('created_at', { ascending: true });
      
      if (error) throw error;
      return data;
    },
  });

  const approveNewsletter = async (id: string) => {
    const { error } = await supabase
      .from('daily_newsletters')
      .update({ status: 'approved' })
      .eq('id', id);

    if (error) {
      toast({
        title: "Error",
        description: "Failed to approve newsletter",
        variant: "destructive",
      });
      return;
    }

    toast({
      title: "Success",
      description: "Newsletter approved successfully",
    });
  };

  const isPastCutoff = () => {
    const now = new Date();
    const cutoffTime = new Date();
    cutoffTime.setHours(9, 0, 0, 0);
    return now > cutoffTime;
  };

  return (
    <Card className="mb-8">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Newsletter Review ({format(new Date(), 'MMM d, yyyy')})</CardTitle>
          <Badge 
            variant={isPastCutoff() ? "destructive" : "success"}
            className="flex items-center gap-2"
          >
            <Clock className="h-4 w-4" />
            {isPastCutoff() ? "Past Cutoff (9 AM)" : "Before Cutoff (9 AM)"}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="text-center py-8">Loading newsletters...</div>
        ) : newsletters?.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No newsletters pending review for today
          </div>
        ) : (
          <div className="space-y-6">
            {newsletters?.map((newsletter) => (
              <Card key={newsletter.id} className="relative">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium">{newsletter.summary}</h3>
                      <p className="text-sm text-muted-foreground">
                        Created at: {format(new Date(newsletter.created_at), 'HH:mm')}
                      </p>
                    </div>
                    <Button
                      onClick={() => approveNewsletter(newsletter.id)}
                      className="flex items-center gap-2"
                      disabled={isPastCutoff()}
                    >
                      <CheckCircle className="h-4 w-4" />
                      Approve
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <NewsletterPreview content={newsletter.content} />
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};